// filepath: c:\Workspace\JDPOX\jdpo-xtreme-ui\src\api\dealer\useDealerApi.ts
// Unpublished Work © 2023-2024 Deere & Company.

import { useContext } from "react";
import { Account } from "./DealerApi";
import {
  DealerAccount,
  DealerAccountLocations,
  DealerCreditDetails,
} from "./types";
import { ApiContext } from "../../context/ApiContext/ApiContext";
import { DelegateContext } from "../../context/DelegateContext/DelegateContext";

const useDealerApi = () => {
  const axios = useContext(ApiContext);
  const { isDelegating } = useContext(DelegateContext);
  const isClientUI = !isDelegating;

  return {
    accountMappedToUser: async (
      serviceURL: string,
      userXid: string,
      dealerAccountId: string
    ): Promise<boolean> =>
      axios
        .getWithRetries(
          `${serviceURL}/api/v1/dealer/accounts/mapping?xid=${userXid}&isClientUI=${isClientUI}`
        )
        .then((response) => response.data)
        .then(
          (accounts: Account[]) =>
            !!accounts &&
            accounts.some((account) => account.id === dealerAccountId)
        )
        .catch((error) => {
          const {
            response: { status },
          } = error;
          return status === 404 || status === 400
            ? Promise.resolve(false)
            : Promise.reject(error);
        }),

    accountsLoader: async (
      serviceURL: string,
      userXid: string
    ): Promise<{ data: Account[] }> =>
      axios.getWithRetries(
        `${serviceURL}/api/v1/dealer/accounts/mapping?xid=${userXid}&isClientUI=${isClientUI}`
      ),

    getAccountsDetails: async (
      url: string,
      accountIds: string[],
      language: string
    ): Promise<DealerAccount[]> => {
      console.log(
        "Fetching account details for IDs from X-ID token:",
        accountIds
      );
      const response = await axios.getWithRetries<DealerAccount[]>(
        `${url}/api/v1/dealer/accounts?accountIds=${accountIds.join(
          ","
        )}&lang=${language}`
      );
      return response.data;
    },

    getDealerAccountLocation: async (
      dealerServiceUrl: string,
      accountIds: string[],
      lang?: string
    ): Promise<DealerAccountLocations> => {
      console.log(
        "Fetching dealer account locations for IDs from useDealerApi:",
        accountIds
      );
      const response = await axios.getWithRetries(
        `${dealerServiceUrl}/api/v1/dealer/accounts/location?accountIds=${accountIds}`,
        {
          params: { lang },
        }
      );
      return { dealerAccountLocations: response.data };
    },

    getDealerCreditDetails: async (
      dealerServiceUrl: string,
      accountId: string,
      lang?: string
    ): Promise<DealerCreditDetails> => {
      const response = await axios.getWithRetries(
        `${dealerServiceUrl}/api/v1/dealer/creditDetails?accountId=${accountId}`,
        {
          params: { lang },
        }
      );
      const dealerCreditDetails = response.data;
      return { availableCreditLimit: dealerCreditDetails.availableCreditLimit };
    },

    fetchDealerUserIds: async (
      serviceURL: string,
      accountId: string,
      accessToken?: string
    ): Promise<string[]> =>
      axios
        .getWithRetries<string[]>(
          `${serviceURL}/api/v1/dealer/${accountId}/userIds?onlyActiveUsers=true`,
          accessToken
            ? { headers: { Authorization: `Bearer ${accessToken}` } }
            : undefined
        )
        .then((response) => response.data),
  };
};

export default useDealerApi;
